"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { AlertCircle, CheckCircle2, Shield, Plus, X, Search, MessageSquare } from "lucide-react";
import combinedData from "../../data/Combined.json";
import ControlSelectorModal from "../payroll/ControlSelectorModal";
import ProcedureSelectorModal from "../payroll/ProcedureSelectorModal";
import InternalControlViewerModal from "../libraries/InternalControlViewerModal";
import {
  associateControlWithRomm,
  disassociateControlFromRomm,
} from "../../utils/payroll-metadata";
import { getAssertionAbbreviation, parseAssertions } from "../../utils/assertion-helpers";
import { ReviewComment } from "../shared/ReviewComment";

interface IARommsProps {
  onBack?: () => void;
}

interface RommItem {
  id: string;
  risk: string;
  assertion: string;
  options: string[];
}

interface InternalControl {
  control_id: string;
  control_name: string;
  control_type: string;
  control_attribute: string;
  control_description: string;
  template?: string;
}

interface SubstantiveProcedure {
  id: string;
  name: string;
  description: string;
  requiredFiles: string[];
  optionalFiles?: string[];
  questions?: Array<{ id: string; question: string; type: string; required: boolean; options?: string[] }>;
}

export default function IARomms({ onBack }: IARommsProps) {
  const [romms, setRomms] = useState<RommItem[]>([]);
  const [selectedRommId, setSelectedRommId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [rommSelections, setRommSelections] = useState<Record<string, string>>({});
  const [rommDocumentation, setRommDocumentation] = useState<Record<string, string>>({});
  const [associatedControls, setAssociatedControls] = useState<Record<string, InternalControl[]>>({});
  const [associatedProcedures, setAssociatedProcedures] = useState<Record<string, SubstantiveProcedure[]>>({});
  const [isControlSelectorOpen, setIsControlSelectorOpen] = useState(false);
  const [isProcedureSelectorOpen, setIsProcedureSelectorOpen] = useState(false);
  const [iaNoteLines, setIaNoteLines] = useState<string[]>([]);
  const [selectedControlForViewing, setSelectedControlForViewing] = useState<any>(null);
  const [controlViewerTemplate, setControlViewerTemplate] = useState<any>(null);
  const [isControlViewerOpen, setIsControlViewerOpen] = useState(false);
  
  // Review comment modal state
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);

  // Load ROMMS data from SharePoint for Intangible Assets
  useEffect(() => {
    const loadRommsData = async () => {
      try {
        if (window.sharePointAPI?.readRommLibraryByWorkspace) {
          const sharePointResponse = await window.sharePointAPI.readRommLibraryByWorkspace("Intangible Assets");
          if (sharePointResponse.success && sharePointResponse.data?.romm_library) {
            const rommsData: RommItem[] = sharePointResponse.data.romm_library.map((entry: any) => ({
              id: entry["romm-id"],
              risk: entry.description,
              assertion: entry.assertion,
              options: ["Lower", "Higher", "Significant", "NRPMM"],
            }));
            setRomms(rommsData);
            if (rommsData.length > 0) setSelectedRommId(rommsData[0].id);

            const savedSelections: Record<string, string> = {};
            const savedDocumentation: Record<string, string> = {};
            sharePointResponse.data.romm_library.forEach((entry: any) => {
              if (entry["assesment"]) savedSelections[entry["romm-id"]] = entry["assesment"]; 
              if (entry["documentation"]) savedDocumentation[entry["romm-id"]] = entry["documentation"]; 
            });
            setRommSelections(savedSelections);
            setRommDocumentation(savedDocumentation);

            // Load associated controls and procedures from Libraries_Romm.json
            const loadedControls: Record<string, InternalControl[]> = {};
            const loadedProcedures: Record<string, SubstantiveProcedure[]> = {};

            // Load controls from Libraries_RCM.json
            if (window.sharePointAPI?.downloadRcmJson) {
              try {
                const rcmRes = await window.sharePointAPI.downloadRcmJson();
                if (rcmRes.success && rcmRes.data) {
                  const rcmData = rcmRes.data;
                  const librariesRcm = rcmData.libraries_RCM || rcmData.libraries_rcm || {};

                  // Process each ROMM entry to load its associated controls
                  for (const entry of sharePointResponse.data.romm_library) {
                    const rommId = entry["romm-id"];
                    
                    // Handle control_id field - can be array, string, or undefined
                    let controlIds: string[] = [];
                    if (entry.control_id !== undefined && entry.control_id !== null) {
                      if (Array.isArray(entry.control_id)) {
                        controlIds = entry.control_id.map((id: any) => String(id).trim()).filter((id: string) => id.length > 0);
                      } else {
                        const singleId = String(entry.control_id).trim();
                        if (singleId.length > 0) {
                          controlIds = [singleId];
                        }
                      }
                    }

                    if (controlIds.length > 0) {
                      const controlsForRomm: InternalControl[] = [];
                      
                      for (const controlId of controlIds) {
                        // Try exact match first
                        let controlEntry = librariesRcm[controlId];
                        
                        // If not found, try case-insensitive match
                        if (!controlEntry) {
                          const matchingKey = Object.keys(librariesRcm).find(
                            key => key.toLowerCase() === controlId.toLowerCase()
                          );
                          if (matchingKey) {
                            controlEntry = librariesRcm[matchingKey];
                          }
                        }
                        
                        if (controlEntry) {
                          controlsForRomm.push({
                            control_id: controlId,
                            control_name: controlEntry.control_name || controlEntry.controlName || controlId,
                            control_type: controlEntry.Nature || controlEntry.subtype || "Unknown",
                            control_attribute: controlEntry.TypeOfControl || controlEntry.typeOfControl || "Unknown",
                            control_description: controlEntry.control_description || controlEntry.control_summary?.control_description || "",
                          });
                        } else {
                          // Control exists in Libraries_Romm.json but not in Libraries_RCM.json
                          // Still add it with basic info so it shows in the UI
                          controlsForRomm.push({
                            control_id: controlId,
                            control_name: controlId,
                            control_type: "Unknown",
                            control_attribute: "Unknown",
                            control_description: `Control ${controlId} (not found in Libraries_RCM.json)`,
                          });
                        }
                      }
                      
                      if (controlsForRomm.length > 0) {
                        loadedControls[rommId] = controlsForRomm;
                      }
                    }
                  }
                }
              } catch (error) {
                console.error("Error loading controls from Libraries_RCM.json:", error);
              }
            }

            // Load procedures
            const procedureNames = sharePointResponse.data.romm_library
              .map((entry: any) => {
                const rommId = entry["romm-id"];
                let procedureIds: string[] = [];
                
                if (entry.procedure_id !== undefined && entry.procedure_id !== null) {
                  if (Array.isArray(entry.procedure_id)) {
                    procedureIds = entry.procedure_id.map((id: any) => String(id).trim()).filter((id: string) => id.length > 0);
                  } else {
                    const singleId = String(entry.procedure_id).trim();
                    if (singleId.length > 0) {
                      procedureIds = [singleId];
                    }
                  }
                }
                
                return {
                  rommId,
                  procedureIds
                };
              })
              .filter((item: any) => item.procedureIds.length > 0);

            // Basic procedure mapping (can be expanded)
            const availableProcedures: Record<string, SubstantiveProcedure> = {};

            for (const item of procedureNames) {
              const proceduresForRomm: SubstantiveProcedure[] = [];
              
              for (const procedureName of item.procedureIds) {
                let procedure = availableProcedures[procedureName];
                
                if (!procedure) {
                  const matchingKey = Object.keys(availableProcedures).find(
                    key => key.toLowerCase() === procedureName.toLowerCase()
                  );
                  if (matchingKey) {
                    procedure = availableProcedures[matchingKey];
                  }
                }
                
                if (!procedure) {
                  procedure = {
                    id: procedureName.toLowerCase().replace(/\s+/g, "_"),
                    name: procedureName,
                    description: `${procedureName} procedure`,
                    requiredFiles: [],
                  };
                }
                
                proceduresForRomm.push(procedure);
              }
              
              if (proceduresForRomm.length > 0) {
                loadedProcedures[item.rommId] = proceduresForRomm;
              }
            }

            setAssociatedControls(loadedControls);
            setAssociatedProcedures(loadedProcedures);
            console.log("Loaded associated controls and procedures from Libraries_Romm.json:", {
              controls: Object.keys(loadedControls).length,
              procedures: Object.keys(loadedProcedures).length
            });

            return;
          }
        }
        // Fallback
        const fallbackData: RommItem[] = [
          { id: "IA.SR.001", risk: "Intangible assets are recorded that did not occur", assertion: "Occurrence", options: ["Lower", "Higher", "Significant", "NRPMM"] },
          { id: "IA.SR.002", risk: "Intangible assets are incomplete", assertion: "Completeness", options: ["Lower", "Higher", "Significant", "NRPMM"] },
          { id: "IA.SR.003", risk: "Intangible assets are recorded in incorrect period", assertion: "Cutoff", options: ["Lower", "Higher", "Significant", "NRPMM"] },
          { id: "IA.SR.004", risk: "Intangible assets are not recorded at proper amount", assertion: "Accuracy", options: ["Lower", "Higher", "Significant", "NRPMM"] },
          { id: "IA.SR.005", risk: "Intangible assets are not recorded in proper account", assertion: "Classification", options: ["Lower", "Higher", "Significant", "NRPMM"] },
        ];
        setRomms(fallbackData);
        if (fallbackData.length > 0) setSelectedRommId(fallbackData[0].id);
      } catch {}
    };
    loadRommsData();
  }, []);

  // Extract IA note lines from Combined.json
  useEffect(() => {
    try {
      const iaEntries = (combinedData as any).data.filter((entry: any) => entry.par === "Intangible Assets");
      const uniqueNoteLines = [...new Set(iaEntries.map((entry: any) => entry.note_line))].filter(Boolean);
      setIaNoteLines(uniqueNoteLines as string[]);
    } catch {}
  }, []);

  const selectedRomm = romms.find((r) => r.id === selectedRommId);

  const handleRommSelection = async (rommId: string, option: string) => {
    setRommSelections((prev) => ({ ...prev, [rommId]: option }));
    try {
      if (window.sharePointAPI?.updateRommEntry) {
        await window.sharePointAPI.updateRommEntry({
          rommId,
          assessment: option,
          documentation: rommDocumentation[rommId] || "",
        });
      }
    } catch {}
  };

  const debouncedAutosave = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (rommId: string, documentation: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          try {
            if (window.sharePointAPI?.updateRommEntry) {
              await window.sharePointAPI.updateRommEntry({
                rommId,
                assessment: rommSelections[rommId] || "",
                documentation,
              });
            }
          } catch {}
        }, 800);
      };
    })(),
    [rommSelections]
  );

  const handleDocumentationChange = (rommId: string, documentation: string) => {
    setRommDocumentation((prev) => ({ ...prev, [rommId]: documentation }));
    debouncedAutosave(rommId, documentation);
  };

  const handleAssociateControls = async (selectedControls: InternalControl[]) => {
    if (!selectedRommId) return;
    const newAssociations = { ...associatedControls, [selectedRommId]: selectedControls };
    setAssociatedControls(newAssociations);
    selectedControls.forEach((control) => {
      associateControlWithRomm(control.control_id, selectedRommId, {
        controlId: control.control_id,
        controlName: control.control_name,
        controlType: control.control_type,
        controlAttribute: control.control_attribute,
        controlDescription: control.control_description,
      });
    });
    try {
      if (window.sharePointAPI?.updateRommEntry) {
        await window.sharePointAPI.updateRommEntry({
          rommId: selectedRommId,
          assessment: rommSelections[selectedRommId] || "",
          documentation: rommDocumentation[selectedRommId] || "",
          controlIds: selectedControls.map((c) => c.control_id),
          procedureIds: associatedProcedures[selectedRommId]?.map((p) => p.name) || [],
        });
      }
    } catch {}
  };

  const handleRemoveControl = async (rommId: string, controlId: string) => {
    const newAssociations = {
      ...associatedControls,
      [rommId]: associatedControls[rommId]?.filter((control) => control.control_id !== controlId) || [],
    };
    setAssociatedControls(newAssociations);
    disassociateControlFromRomm(controlId, rommId);
    try {
      if (window.sharePointAPI?.updateRommEntry) {
        await window.sharePointAPI.updateRommEntry({
          rommId,
          assessment: rommSelections[rommId] || "",
          documentation: rommDocumentation[rommId] || "",
          controlIds: newAssociations[rommId]?.map((c) => c.control_id) || [],
          procedureIds: associatedProcedures[rommId]?.map((p) => p.name) || [],
        });
      }
    } catch {}
  };

  const handleAssociateProcedures = async (selectedProcedures: SubstantiveProcedure[]) => {
    if (!selectedRommId) return;
    setAssociatedProcedures(prev => ({ ...prev, [selectedRommId]: selectedProcedures }));
    try {
      if (window.sharePointAPI?.updateRommEntry) {
        await window.sharePointAPI.updateRommEntry({
          rommId: selectedRommId,
          assessment: rommSelections[selectedRommId] || "",
          documentation: rommDocumentation[selectedRommId] || "",
          controlIds: associatedControls[selectedRommId]?.map((c) => c.control_id) || [],
          procedureIds: selectedProcedures.map((p) => p.name),
        });
      }
    } catch {}
  };

  const handleRemoveProcedure = async (rommId: string, procedureId: string) => {
    const updatedProcedures = {
      ...associatedProcedures,
      [rommId]: associatedProcedures[rommId]?.filter((procedure) => procedure.id !== procedureId) || [],
    };
    setAssociatedProcedures(updatedProcedures);
    try {
      if (window.sharePointAPI?.updateRommEntry) {
        await window.sharePointAPI.updateRommEntry({
          rommId,
          assessment: rommSelections[rommId] || "",
          documentation: rommDocumentation[rommId] || "",
          controlIds: associatedControls[rommId]?.map((c) => c.control_id) || [],
          procedureIds: updatedProcedures[rommId]?.map((p) => p.name) || [],
        });
      }
    } catch {}
  };

  const isRommComplete = (rommId: string) => rommSelections[rommId] !== undefined;
  const allRommsComplete = romms.every((r) => isRommComplete(r.id));

  const getRiskLevelColor = (option: string) => {
    switch (option) {
      case "Lower":
        return "bg-yellow-300/20 text-yellow-300 border-yellow-300/30";
      case "Higher":
        return "bg-yellow-700/30 text-yellow-400 border-yellow-700/30";
      case "Significant":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "NRPMM":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Risk of Material Misstatement (RoMMs) - Intangible Assets</h2>
          <p className="text-sm text-white/60">Assess the risk level for Intangible Assets assertions</p>
        </div>
      </div>

      {/* Two Column Layout - 30% Left, 70% Right */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left Panel - ROMMS List */}
        <Card className="border-white/10 bg-white/5 text-white lg:w-[30%] lg:flex-shrink-0">
          <CardHeader>
            <CardTitle className="text-lg">Intangible Assets Risks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input
                type="text"
                placeholder="Search ROMMs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/5 border-white/20 pl-9 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20"
              />
            </div>
            {/* Filtered ROMMs List */}
            {romms
              .filter((romm) => {
                const query = searchQuery.toLowerCase();
                return (
                  romm.id.toLowerCase().includes(query) ||
                  romm.risk.toLowerCase().includes(query) ||
                  romm.assertion.toLowerCase().includes(query)
                );
              })
              .map((romm) => (
              <div
                key={romm.id}
                className={`cursor-pointer rounded-lg border p-3 transition-all ${
                  selectedRommId === romm.id
                    ? "border-blue-500/50 bg-blue-500/10"
                    : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                }`}
                onClick={() => setSelectedRommId(romm.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{romm.id}</Badge>
                      {isRommComplete(romm.id) ? (
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-yellow-400" />
                      )}
                    </div>
                    <p className="text-sm leading-relaxed text-white/80">{romm.risk}</p>
                  </div>
                </div>
              </div>
              ))}
            {romms.filter((romm) => {
              const query = searchQuery.toLowerCase();
              return (
                romm.id.toLowerCase().includes(query) ||
                romm.risk.toLowerCase().includes(query) ||
                romm.assertion.toLowerCase().includes(query)
              );
            }).length === 0 && searchQuery && (
              <div className="text-center py-6 text-white/60 text-sm">
                No ROMMs found matching "{searchQuery}"
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Panel - Four Column Layout */}
        <Card className="border-white/10 bg-white/5 text-white lg:w-[70%] lg:flex-shrink-0">
          <CardHeader>
            <CardTitle className="text-lg">{selectedRomm?.id || "Select a ROMM"}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Table Header */}
            <div className="mb-4 hidden rounded-lg border border-white/20 bg-white/10 p-3 md:grid md:grid-cols-4 md:gap-4">
              <div className="text-sm font-semibold text-white">Description</div>
              <div className="text-sm font-semibold text-white">Accounts/Areas</div>
              <div className="text-sm font-semibold text-white">Assertions/Type</div>
              <div className="text-sm font-semibold text-white">Assessment</div>
            </div>

            {/* Table Rows - Show only selected ROMM */}
            <div className="space-y-4">
              {selectedRomm && (
                <div className="rounded-lg border border-white/10 bg-white/5 p-4 transition-colors hover:bg-white/10 md:grid md:grid-cols-4 md:gap-4 md:p-3">
                  {/* Description Column */}
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Description</div>
                    <div className="text-sm text-white/80">{selectedRomm.risk}</div>
                  </div>

                  {/* Accounts/Areas Column */}
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Accounts/Areas</div>
                    <div className="space-y-1">
                      {iaNoteLines.length > 0 ? (
                        iaNoteLines.map((noteLine, index) => (
                          <div key={index} className="flex items-center gap-1 text-xs text-orange-400">
                            <span className="h-2 w-2 rounded-full bg-orange-400"></span>
                            {noteLine}
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-white/60">Loading accounts/areas...</div>
                      )}
                    </div>
                  </div>

                  {/* Assertions/Type Column */}
                  <div className="mb-4 md:mb-0">
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Assertions/Type</div>
                    <div className="space-y-2">
                      {parseAssertions(selectedRomm.assertion).map((assertion, index) => (
                        <div key={index} className="flex items-center">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                            {getAssertionAbbreviation(assertion)}
                          </div>
                          <span className="ml-2 text-sm text-white">{assertion}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Assessment Column */}
                  <div>
                    <div className="mb-2 block text-xs font-semibold text-white/60 md:hidden">Assessment</div>
                    <div className="flex items-center gap-2">
                      <Select value={rommSelections[selectedRomm.id] || ""} onValueChange={(value) => handleRommSelection(selectedRomm.id, value)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select assessment" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedRomm.options.map((option) => (
                            <SelectItem key={option} value={option}>
                              <div className="flex items-center gap-2">
                                <Badge className={`text-xs ${getRiskLevelColor(option)}`}>{option}</Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Risk Assessment Documentation */}
            {selectedRomm && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="text-md font-medium text-white">Risk Assessment Documentation</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReviewCommentOpen(true)}
                      className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
                      aria-label="Review Comments"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    onClick={async () => {
                      if (!selectedRommId) return;
                      try {
                        if (window.sharePointAPI?.updateRommEntry) {
                          await window.sharePointAPI.updateRommEntry({
                            rommId: selectedRommId,
                            assessment: rommSelections[selectedRommId] || "",
                            documentation: rommDocumentation[selectedRommId] || "",
                            controlIds: associatedControls[selectedRommId]?.map((c) => c.control_id) || [],
                            procedureIds: associatedProcedures[selectedRommId]?.map((p) => p.name) || [],
                          });
                        }
                      } catch {}
                    }}
                    className="bg-green-600 text-white hover:bg-green-700"
                    disabled={!rommSelections[selectedRomm.id]}
                  >
                    Submit to SharePoint
                  </Button>
                </div>
                <Textarea
                  placeholder="Enter your risk assessment documentation here..."
                  value={rommDocumentation[selectedRomm.id] || ""}
                  onChange={(e) => handleDocumentationChange(selectedRomm.id, e.target.value)}
                  className="min-h[120px] bg-white/5 border-white/20 text-white placeholder:text-white/50 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
            )}

            {/* Associated Controls */}
            {selectedRomm && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-medium text-white flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Associated Controls
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsControlSelectorOpen(true)}
                    className="flex items-center gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10"
                  >
                    <Plus className="h-4 w-4" />
                    Associate Control
                  </Button>
                </div>

                {associatedControls[selectedRomm.id] && associatedControls[selectedRomm.id].length > 0 ? (
                  <div className="space-y-2">
                    {associatedControls[selectedRomm.id].map((control) => (
                      <Card key={control.control_id} className="border-white/10 bg-white/5 cursor-pointer transition-all hover:border-white/20 hover:bg-white/10">
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1" onClick={() => {
                              setSelectedControlForViewing(control);
                              setControlViewerTemplate(null);
                              setIsControlViewerOpen(true);
                            }}>
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="text-sm font-medium text-white">{control.control_name}</h5>
                                <Badge variant="outline" className="text-xs">{control.control_id}</Badge>
                              </div>
                              <p className="text-xs text-white/70 mb-2">{control.control_description}</p>
                              <div className="flex gap-2">
                                <Badge variant="outline" className="text-xs">{control.control_type}</Badge>
                                <Badge variant="outline" className="text-xs">{control.control_attribute}</Badge>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveControl(selectedRomm.id, control.control_id)}
                              className="text-white/60 hover:text-white hover:bg-white/10"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-white/60 border border-white/10 rounded-lg bg-white/5">
                    <Shield className="h-8 w-8 mx-auto mb-2 text-white/40" />
                    <p className="text-sm">No controls associated with this ROMM</p>
                    <p className="text-xs mt-1">Click "Associate Control" to add controls from the Internal Control Library</p>
                  </div>
                )}
              </div>
            )}

            {/* Substantive Procedures */}
            {selectedRomm && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-medium text-white flex items-center gap-2">Substantive Procedures</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsProcedureSelectorOpen(true)}
                    className="flex items-center gap-2 border-white/20 bg-white/5 text-white hover:bg-white/10"
                  >
                    <Plus className="h-4 w-4" />
                    Associate Procedure
                  </Button>
                </div>
                {associatedProcedures[selectedRomm.id] && associatedProcedures[selectedRomm.id].length > 0 ? (
                  <div className="space-y-2">
                    {associatedProcedures[selectedRomm.id].map((procedure) => (
                      <Card key={procedure.id} className="border-white/10 bg-white/5">
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="text-sm font-medium text-white">{procedure.name}</h5>
                                <Badge variant="outline" className="text-xs">{procedure.id}</Badge>
                              </div>
                              <p className="text-xs text-white/70 mb-2">{procedure.description}</p>
                              <div className="space-y-1 mb-2">
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-white/60">Required Files:</span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {procedure.requiredFiles.map((file, index) => (
                                    <Badge key={index} variant="outline" className="text-xs">{file}</Badge>
                                  ))}
                                </div>
                              </div>
                              {procedure.questions && procedure.questions.length > 0 && (
                                <Badge variant="outline" className="text-xs">{procedure.questions.length} question(s)</Badge>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveProcedure(selectedRomm.id, procedure.id)}
                              className="text-white/60 hover:text-white hover:bg-white/10"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-white/60 border border-white/10 rounded-lg bg-white/5">
                    <p className="text-sm">No procedures associated with this ROMM</p>
                    <p className="text-xs mt-1">Click "Associate Procedure" to add procedures from Substantive Procedures</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-4">
        <div className="text-sm text-white/60">Complete all risk assessments to proceed to the next step</div>
        <Button disabled={!allRommsComplete} className="bg-blue-600 text-white hover:bg-blue-700">Complete Risk Assessment</Button>
      </div>

      {/* Control Selector Modal */}
      <ControlSelectorModal
        isOpen={isControlSelectorOpen}
        onClose={() => setIsControlSelectorOpen(false)}
        onControlsSelected={handleAssociateControls}
        existingControls={selectedRommId ? associatedControls[selectedRommId] || [] : []}
      />

      {/* Procedure Selector Modal */}
      <ProcedureSelectorModal
        isOpen={isProcedureSelectorOpen}
        onClose={() => setIsProcedureSelectorOpen(false)}
        onProceduresSelected={handleAssociateProcedures}
        existingProcedures={selectedRommId ? associatedProcedures[selectedRommId] || [] : []}
      />

      {/* Control Viewer Modal */}
      <InternalControlViewerModal
        isOpen={isControlViewerOpen}
        onClose={() => setIsControlViewerOpen(false)}
        onSave={() => {}}
        control={selectedControlForViewing}
        template={controlViewerTemplate}
        mode="view"
      />

      {/* Review Comment Modal */}
      {selectedRommId && (
        <ReviewComment
          questionId={selectedRommId}
          questionText={`Risk Assessment Documentation - ${selectedRommId}`}
          isOpen={reviewCommentOpen}
          onClose={() => setReviewCommentOpen(false)}
        />
      )}
    </div>
  );
}


